import { config } from "../config";
import { flyMapTo } from "./worldSearch";
import {
  loadPolygonInteractiveLib,
  type Map3dElement,
} from "./worldSoldPixels";

export const REGION_PREVIEW_MARKER = "data-dwik-region-preview";

export interface RegionPreviewPayload {
  id: string;
  name: string;
  is_global?: boolean;
  min_lat: number;
  max_lat: number;
  min_lng: number;
  max_lng: number;
  /** GeoJSON-style ring: [lng, lat] pairs */
  original_geojson?: Array<[number, number]>;
}

export function cameraForRegionBounds(bounds: {
  min_lat: number;
  max_lat: number;
  min_lng: number;
  max_lng: number;
}): { lat: number; lng: number; range: number; altitude: number } {
  const centerLat = (bounds.min_lat + bounds.max_lat) / 2;
  const centerLng = (bounds.min_lng + bounds.max_lng) / 2;
  const latSpan = Math.max(bounds.max_lat - bounds.min_lat, 0.0005);
  const lngSpan = Math.max(bounds.max_lng - bounds.min_lng, 0.0005);
  const latM = latSpan * 111_320;
  const lngM =
    lngSpan * 111_320 * Math.cos((centerLat * Math.PI) / 180);
  const span = Math.max(latM, lngM, 400);
  const range = Math.min(Math.max(span * 2.4, 1200), 2_500_000);
  return {
    lat: centerLat,
    lng: centerLng,
    range,
    altitude: config.pixelMeshBaseAltitude + 2000,
  };
}

function geojsonToPath(
  ring: Array<[number, number]>,
): google.maps.maps3d.LatLngAltitudeLiteral[] {
  const altitude = config.pixelMeshBaseAltitude + config.pixelOverlayAltitudeOffset;
  return ring.map(([lng, lat]) => ({ lng, lat, altitude }));
}

export function clearRegionPreview(mapEl: Map3dElement): void {
  mapEl.querySelectorAll(`[${REGION_PREVIEW_MARKER}]`).forEach((node) => {
    try {
      mapEl.removeChild(node);
    } catch {
      /* detached */
    }
  });
}

export async function showRegionPreviewOnMap(
  mapEl: Map3dElement,
  payload: RegionPreviewPayload,
): Promise<void> {
  const cam = cameraForRegionBounds(payload);
  flyMapTo(mapEl as unknown as google.maps.maps3d.Map3DElement, cam.lat, cam.lng, {
    range: cam.range,
    altitude: cam.altitude,
    durationMillis: 1400,
  });

  clearRegionPreview(mapEl);

  const ring = payload.original_geojson;
  if (payload.is_global || !ring || ring.length < 3) {
    return;
  }

  const loaded = await loadPolygonInteractiveLib();
  if (!loaded || !mapEl.isConnected) return;

  const { PolyCtor, altitudeMode } = loaded;
  const path = geojsonToPath(ring);
  const gon = new PolyCtor({
    altitudeMode,
    fillColor: "rgba(59, 110, 129, 0.35)",
    path,
    strokeColor: "rgba(255, 210, 80, 0.95)",
    strokeWidth: 3,
    drawsOccludedSegments: true,
    extruded: true,
    geodesic: false,
    zIndex: 5,
  });
  gon.setAttribute(REGION_PREVIEW_MARKER, "1");
  gon.setAttribute("title", payload.name);
  mapEl.append(gon);
}

export function attachRegionPreviewLayer(
  mapEl: Map3dElement,
  payload: RegionPreviewPayload | null,
): () => void {
  if (!payload) return () => undefined;
  let cancelled = false;
  void showRegionPreviewOnMap(mapEl, payload).then(() => {
    if (cancelled) clearRegionPreview(mapEl);
  });
  return () => {
    cancelled = true;
    clearRegionPreview(mapEl);
  };
}
